export enum CoreSpaceType {
  SPACE = 'space',
  FLOOR = 'floor',
  BUILDING = 'building',
  CAMPUS = 'campus',
}

export enum CoreSpaceFunction {
  BREAK_ROOM = 'break_room',
  CAFE = 'cafe',
  COLLABORATION = 'collaboration',
  CONFERENCE_ROOM = 'conference_room',
  DINING_AREA = 'dining_area',
  EVENT_SPACE = 'event_space',
  FOCUS_QUIET = 'focus_quiet',
  GYM = 'gym',
  KITCHEN = 'kitchen',
  LAB = 'lab',
  LIBRARY = 'library',
  LOUNGE = 'lounge',
  MEETING_ROOM = 'meeting_room',
  OFFICE = 'office',
  PHONE_BOOTH = 'phone_booth',
  PLACE_OF_WORSHIP = 'place_of_worship',
  RECEPTION = 'reception',
  RESTROOM = 'restroom',
  RETAIL = 'retail',
  THEATER = 'theater',
  WELLNESS_ROOM = 'wellness_room',
  WORKSTATIONS = 'workstations',
}

export enum CoreSpaceSizeAreaUnit {
  SQUARE_FEET = 'square_feet',
  SQUARE_METERS = 'square_meters',
}

export enum CoreSpaceSafeThresholdMode {
  SAFE_CAPACITY = 'SAFE_CAPACITY',
  UTILIZATION_PERC_LEGAL = 'UTILIZATION_PERC_LEGAL',
  UTILIZATION_PERC_TARGET = 'UTILIZATION_PERC_TARGET',
}

export enum CoreSpaceCountingMode {
  NONE = 'none',
  DOORWAYS = 'doorways',
  SUM_EXPLICIT = 'sum_explicit',
  SUM_CHILDREN = 'sum_children',
}

export type CoreSpaceAncestor = {
  id: string;
  name: string;
};

export type CoreSpaceAssignedTeam = {
  id: string;
  name: string;
};

export type CoreSpaceServiceMapping = {
  id: string;
  service_id: string;
  space_id: string;
  service_space_id: string;
  service_space_name: string | null;
};

export type CoreSpaceTimeSegment = {
  id: string;
  label: string;
  start: string;
  end: string;
  days: Array<string>;
};

export type CoreSpaceDoorway = {
  id: string;
  name: string;
  link_id: string;
  sensor_placement: 1 | -1;
};

export type CoreSpaceLabel = {
  id: string;
  name: string;
  spaces: Array<Pick<CoreSpace, 'id' | 'name'>>;
};

// Space data returned by GET /v2/spaces
export type CoreSpace = {
  id: string;
  name: string;
  notes: string | null;
  space_type: CoreSpaceType;
  function: CoreSpaceFunction | null;
  time_zone: string; // "America/New_York"
  daily_reset: string; // "04:00"
  status: 'planning' | 'live';
  current_count: number;
  capacity: number | null;
  target_capacity: number | null;
  max_dwell_minutes: number | null;
  parent_id: string | null;
  ancestry: Array<CoreSpaceAncestor>;
  address: string | null;
  latitude: number | null;
  longitude: number | null;
  floor_level: number | null;
  space_mappings: Array<CoreSpaceServiceMapping>;
  doorways: Array<CoreSpaceDoorway>;
  tags: Array<string>;
  time_segments: Array<CoreSpaceTimeSegment>;
  time_segment_groups: Array<unknown>; // DEPRECATED, do not use
  size_area: number | null;
  size_area_unit: CoreSpaceSizeAreaUnit | null;
  annual_rent: number | null;
  annual_rent_currency: 'USD' | null;
  assigned_teams: Array<CoreSpaceAssignedTeam>;
  sensors_total: number;
  image_url: string | null;
  created_at: string;
  updated_at: string;
  description: string | null;
  inherits_time_segments: boolean;
  safe_capacity: number | null;
  safe_threshold_mode: CoreSpaceSafeThresholdMode | null;
  safe_threshold_value: number | null;
  counting_mode: CoreSpaceCountingMode;
  component_spaces: Array<CoreSpace['id']>;
  labels: CoreSpaceLabel[];
};

// Recursive space hierarchy returned by GET /v2/spaces/hierarchy
export type CoreSpaceHierarchyNode = {
  id: string;
  name: string;
  has_purview: boolean;
  space_type: CoreSpaceType;
  daily_reset: string;
  inherits_time_segments: boolean;
  time_segments: Array<CoreSpaceTimeSegment>;
  capacity: number | null;
  children?: Array<CoreSpaceHierarchyNode>;
};
